import { CircularProgress, Slider, styled } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import colors from "../../theme/themeConstants";
import { metrics } from "utils/metrics";

interface IProps {
  video: any;
  newDuration: number[];
  setNewDuration: (duration: number[]) => void;
  timeLineURL: string;
  playerRef: any;
  fromUrl: string;
}

const MAX_DISTANCE = 60;

const VideoTrimmer = (props: IProps) => {
  const { video, newDuration, setNewDuration, timeLineURL, playerRef, fromUrl } = props;

  const [duration, setDuration] = useState<number[]>(newDuration);

  const handleNewDuration = (e: any, newValue: any, activeThumb: any) => {
    if (newValue[1] - newValue[0] > MAX_DISTANCE) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], duration[1] - MAX_DISTANCE);
        setNewDuration([clamped, clamped + MAX_DISTANCE]);
        playerRef.current.seekTo(clamped, "seconds");
      } else {
        const clamped = Math.max(newValue[1], MAX_DISTANCE);
        setNewDuration([clamped - MAX_DISTANCE, clamped]);
        playerRef.current.seekTo(clamped - MAX_DISTANCE, "seconds");
      }
    } else {
      setNewDuration(newValue as number[]);
      playerRef.current.seekTo(newValue[0], "seconds");
    }
  };

  const handleChangeCommitted = useCallback(async () => {
    await metrics.sendUploadVideoTrim();
  }, []);

  function setFileInfo(file: any) {
    const newVideo = document.createElement("video");
    newVideo.preload = "metadata";

    newVideo.onloadedmetadata = function () {
      window.URL.revokeObjectURL(newVideo.src);
      setDuration([0, Number(newVideo.duration.toExponential(2))]);
      if (newDuration[1] === 0) {
        setNewDuration([0, Number(newVideo.duration.toExponential(2))]);
      }
    };
    newVideo.src = typeof file === "string" ? file : URL.createObjectURL(file);
  }

  const setInitialLength = () => {
    const setInitLength = newDuration[1] > MAX_DISTANCE ? MAX_DISTANCE : newDuration[1];
    setNewDuration([0, setInitLength]);
  };

  useEffect(() => {
    setFileInfo(fromUrl || video);
    // eslint-disable-next-line
  }, [fromUrl, video]);

  useEffect(() => {
    setInitialLength();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  return (
    <Container>
      <Slider
        value={newDuration}
        onChange={handleNewDuration}
        onChangeCommitted={handleChangeCommitted}
        min={0}
        max={duration[1]}
        disabled={timeLineURL ? false : true}
        step={0.10}
        valueLabelDisplay="auto"
        sx={{
          "& .MuiSlider-thumb": {
            zIndex: 10,
            height: "10px !important",
            minHeight: "unset",
            width: "10px",
            color: "white"
          },
          "& .MuiSlider-track": {
            zIndex: 10,
            backgroundColor: "transparent",
            height: "50px",
            border: "2px solid white",
            borderRadius: "8px"
          },
          "& .MuiSlider-rail": {
            backgroundColor: "rgba(0, 0, 0, .7)",
            zIndex: 9,
            height: "54px",
          },
          "& .MuiSlider-thumb:hover, & .MuiSlider-thumb.Mui-focusVisible": {
            boxShadow: "0px 0px 0px 8px rgba(255, 255, 255, 0.16)"
          }
        }}
      />
      {/* eslint-disable-next-line */}
      {timeLineURL ? <LazyLoadImage src={timeLineURL}/>
        : <CircularProgress sx={{ position: "absolute", color: colors.white }}/>}
    </Container>
  );
};

export default VideoTrimmer;

const Container = styled("div")({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginTop: "44px",
  marginBottom: "24px",
  padding: "4px 0",
  backgroundColor: "black",
  minHeight: "48px",
  "& img": {
    width: "100%",
    position: "absolute",
    height: "48px",
    borderRadius: "8px",
    objectFit: "cover"
  },
  "@media screen and (max-width: 767px)": {
    marginTop: "2rem",
    marginBottom: "0"
  }
});
